import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Shield } from "lucide-react";
import bandflowAvatar from "@/assets/bandflow-avatar.png";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const checkAdmin = async () => {
      const { data } = await supabase.rpc("is_admin_or_moderator", { _user_id: user.id });
      if (data) {
        navigate("/admin/venues");
      } else {
        toast({ title: "Acceso denegado", description: "No tienes permisos de administración.", variant: "destructive" });
        await supabase.auth.signOut();
      }
    };
    checkAdmin();
  }, [user, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      const { data: isAdmin } = await supabase.rpc("is_admin_or_moderator", { _user_id: signInData.user.id });
      if (!isAdmin) {
        await supabase.auth.signOut();
        throw new Error("No tienes permisos de administración.");
      }

      const { data: profileData } = await supabase.from("profiles").select("account_status").eq("id", signInData.user.id).single();
      if ((profileData as any)?.account_status === "blocked") {
        await supabase.auth.signOut();
        throw new Error("Tu cuenta ha sido bloqueada.");
      }

      toast({ title: "Bienvenido al panel de administración" });
      navigate("/admin/venues");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-background border">
            <img src={bandflowAvatar} alt="Bandflow" className="h-6 w-6" />
          </div>
          <CardTitle className="font-display text-xl">Administración</CardTitle>
          <CardDescription>Accede al panel de gestión</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input id="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@ejemplo.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Contraseña</Label>
              <Input id="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Verificando..." : "Acceder"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
